// Services Data for SEO-Optimized Service Pages

export interface ServiceType {
  slug: string;
  name: string;
  shortName: string;
  tagline: string;
  description: string;
  features: string[];
  benefits: string[];
  idealFor: string[];
  startingPrice?: string;
  turnaround: string;
  icon: string;
  faqs?: { question: string; answer: string }[];
}

export const services: ServiceType[] = [
  {
    slug: "real-estate-photography",
    name: "Real Estate Photography",
    shortName: "Photography",
    tagline: "Magazine-Quality Images That Sell",
    description: "Professional HDR photography that captures every detail of your property. Our expert photographers use state-of-the-art equipment and lighting techniques to create stunning images that make listings stand out and attract qualified buyers.",
    features: [
      "HDR photography with professional editing",
      "Twilight and golden hour shoots",
      "Interior and exterior coverage",
      "Wide-angle lens for spacious feel",
      "Professional color correction",
      "Same-day or next-day delivery",
    ],
    benefits: [
      "Listings get 118% more views",
      "Properties sell 32% faster",
      "Higher perceived property value",
      "Professional first impression",
    ],
    idealFor: [
      "Residential homes",
      "Luxury properties",
      "Commercial spaces",
      "New constructions",
    ],
    startingPrice: "$250",
    turnaround: "24-48 hours",
    icon: "Camera",
    faqs: [
      { question: "How much does real estate photography cost?", answer: "Real estate photography packages start at $250. We offer bundled pricing for photo + drone + video — request a quote for your specific property and we'll send options within a few hours." },
      { question: "How long does a real estate photo shoot take?", answer: "Most residential shoots take 60-90 minutes. Larger or luxury properties may run 2-3 hours. We'll give you a time estimate when you book." },
      { question: "How quickly do I get the photos back?", answer: "Standard delivery is 24-48 hours after the shoot. Rush delivery (same-day or next morning) is available for an additional fee." },
      { question: "Do you shoot twilight and golden hour photos?", answer: "Yes. Twilight and dusk shoots are available and produce dramatic results — especially for exterior shots and luxury listings. These are scheduled separately and can be added to any package." },
      { question: "What areas do you serve?", answer: "We serve Washington State (Greater Puget Sound, South Sound, Olympic Peninsula, and surrounding areas) and Central Arkansas (Little Rock metro and surrounding areas)." },
    ],
  },
  {
    slug: "real-estate-video",
    name: "Real Estate Video Tours",
    shortName: "Video",
    tagline: "Cinematic Tours That Tell a Story",
    description: "Cinematic video tours that create emotional connections with potential buyers. Our professional videographers craft smooth, engaging walkthroughs with premium stabilization, music, and editing that showcase your property's best features.",
    features: [
      "4K cinematic quality",
      "Gimbal-stabilized footage",
      "Professional music licensing",
      "Voiceover options available",
      "Social media optimized cuts",
      "YouTube-ready formatting",
    ],
    benefits: [
      "403% more inquiries on listings",
      "Buyers spend 5x more time viewing",
      "Perfect for remote buyers",
      "Shareable on all platforms",
    ],
    idealFor: [
      "Luxury homes",
      "Large estates",
      "Unique properties",
      "New developments",
    ],
    startingPrice: "$450",
    turnaround: "3-5 business days",
    icon: "Video",
    faqs: [
      { question: "How much does a real estate video tour cost?", answer: "Real estate video tours start at $450. Pricing varies based on property size, video length, and package inclusions. We offer bundled rates when combined with photography or drone." },
      { question: "What does a real estate video tour include?", answer: "Every video includes 4K gimbal-stabilized walkthrough footage, professional color grading, licensed background music, and a finished edit ready for MLS, social media, and YouTube. Voiceover and agent intro clips are available as add-ons." },
      { question: "How long is the finished video?", answer: "Standard listing videos run 2-4 minutes. We also deliver a 60-second social cut optimized for Instagram and Facebook Reels. Longer versions are available for luxury or commercial properties." },
      { question: "How long does it take to get the video back?", answer: "Most videos are delivered within 3-5 business days. Rush edits are available — ask when booking." },
      { question: "Do real estate videos actually help sell homes?", answer: "Yes. Listings with video get 403% more inquiries than those without. Video is especially effective for out-of-area buyers, luxury properties, and unique homes that benefit from context a photo can't provide." },
    ],
  },
  {
    slug: "drone-photography",
    name: "Drone & Aerial Photography",
    shortName: "Drone",
    tagline: "Stunning Perspectives From Above",
    description: "FAA-certified drone operators capture breathtaking aerial views that showcase your property, land, and surrounding neighborhood. Perfect for highlighting lot size, location, and community amenities.",
    features: [
      "4K aerial video & photography",
      "FAA Part 107 certified pilots",
      "Property boundary shots",
      "Neighborhood context views",
      "Elevation and terrain coverage",
      "Weather-flexible scheduling",
    ],
    benefits: [
      "Showcase lot size and layout",
      "Highlight location advantages",
      "Show proximity to amenities",
      "Differentiate from competitors",
    ],
    idealFor: [
      "Large properties",
      "Waterfront homes",
      "Rural estates",
      "Commercial developments",
    ],
    startingPrice: "$225",
    turnaround: "24-48 hours",
    icon: "Plane",
  },
  {
    slug: "virtual-tours",
    name: "3D Virtual Tours",
    shortName: "Virtual Tours",
    tagline: "Interactive Experiences Anytime, Anywhere",
    description: "Immersive 3D virtual tours that let buyers explore properties from anywhere in the world. Using Matterport technology, we create dollhouse views and floor plans that provide a complete understanding of the space.",
    features: [
      "Matterport 3D scanning",
      "Interactive dollhouse view",
      "Schematic floor plans",
      "Measurement tools",
      "VR headset compatible",
      "Embeddable tour links",
    ],
    benefits: [
      "24/7 open house availability",
      "Pre-qualify remote buyers",
      "Reduce unnecessary showings",
      "Stand out in MLS listings",
    ],
    idealFor: [
      "Out-of-state buyers",
      "Luxury properties",
      "Commercial spaces",
      "Vacation rentals",
    ],
    startingPrice: "$250",
    turnaround: "2-3 business days",
    icon: "Eye",
  },
  {
    slug: "floor-plans",
    name: "Floor Plans",
    shortName: "Floor Plans",
    tagline: "Professional Layouts That Clarify Space",
    description: "Accurate 2D and 3D floor plans that help buyers understand the flow and dimensions of your property. Essential for serious buyers making informed decisions.",
    features: [
      "2D schematic floor plans",
      "3D rendered floor plans",
      "Accurate measurements",
      "Room labels and dimensions",
      "Multiple export formats",
      "Branded customization",
    ],
    benefits: [
      "52% of buyers find them essential",
      "Reduce buyer confusion",
      "Professional marketing tool",
      "MLS-ready formats",
    ],
    idealFor: [
      "All residential properties",
      "Multi-level homes",
      "Unique layouts",
      "Commercial properties",
    ],
    startingPrice: "$100",
    turnaround: "24-48 hours",
    icon: "LayoutDashboard",
  },
  {
    slug: "commercial-architecture",
    name: "Commercial & Architecture Photography",
    shortName: "Commercial",
    tagline: "Showcase Spaces That Mean Business",
    description: "Professional photography and video for commercial properties, office buildings, retail spaces, restaurants, and architectural projects. We highlight design details, scale, and functionality to attract tenants, buyers, and investors.",
    features: [
      "Interior and exterior architectural shots",
      "Twilight and ambient lighting setups",
      "Drone aerials for commercial sites",
      "Construction progress documentation",
      "Tenant-ready marketing imagery",
      "Large-format print-ready files",
    ],
    benefits: [
      "Attract qualified commercial tenants",
      "Showcase architectural design intent",
      "Support investor presentations",
      "Elevate commercial listings above competitors",
    ],
    idealFor: [
      "Office buildings",
      "Retail spaces",
      "Restaurants & hospitality",
      "New construction projects",
    ],
    startingPrice: "$350",
    turnaround: "3-5 business days",
    icon: "Building2",
  },
  {
    slug: "agent-branding",
    name: "Headshots & Agent Branding",
    shortName: "Headshots",
    tagline: "Build Your Professional Image",
    description: "Professional headshots, team photos, and branded marketing content that establish your personal brand and build trust with clients. Stand out in a competitive market with consistent, polished imagery.",
    features: [
      "Professional headshots",
      "Team photography",
      "Branded video intros",
      "Social media content",
      "Business card photography",
      "Website imagery",
    ],
    benefits: [
      "Build client trust instantly",
      "Consistent brand identity",
      "Stand out from competitors",
      "Professional online presence",
    ],
    idealFor: [
      "Real estate agents",
      "Brokerages",
      "Property managers",
      "Real estate teams",
    ],
    startingPrice: "$199",
    turnaround: "3-5 business days",
    icon: "User",
  },
];

export const getServiceBySlug = (slug: string): ServiceType | undefined => {
  return services.find((service) => service.slug === slug);
};
